import React from 'react';

// Types
import { GoalItem } from 'Shared/constants/types';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useToggle } from 'Shared/utils/hooks';
import { useCopyToClipboard } from 'Shared/utils/hooks/use-copy-to-clipboard';

// Components
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import GoalDeleteConfirmationModal from '../GoalDeleteConfirmationModal/GoalDeleteConfirmationModal';
import GoalSettingsModal from '../GoalSettingsModal/GoalSettingsModal';
import CreateGoalModal from 'Dashboard/components/CreateOrUpdateGoalPageV3/CreateGoalModal';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  goal: GoalItem;
};

const GoalActionsDropdown = ({ goal }: Props) => {
  const { canChangeStatus } = useGoal({ slug: goal.slug });
  const { tr } = useTranslation();

  const [isDeleteOpen, toggleIsDeleteOpen] = useToggle(false);
  const [isSettingsOpen, toggleIsSettingsOpen] = useToggle(false);
  const [isCloneOpen, toggleIsCloneOpen] = useToggle(false);

  const { onCopyTextToClipboard: handleCopyLinkClick } = useCopyToClipboard({
    textToCopy: `${window.location.origin}${PageUrlsUtil.makeGoalDetailPageUrl(goal.slug)}`,
  });

  return (
    <>
      <WpDropdown placement="bottom-end">
        <WpDropdownToggle>
          <WpButton qaTest="goals-actions-dropdown" size="sm" variant="tertiary" square>
            <WpIcon name="ellipsis-h-alt" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          <WpDropdownMenuItem onClick={handleCopyLinkClick}>
            <WpIcon name="link" />
            {tr('perform.goals.actions.copy_link')}
          </WpDropdownMenuItem>
          <WpDropdownMenuItem onClick={toggleIsCloneOpen}>
            <WpIcon name="copy" size="sm" />
            {tr('perform.goals.actions.duplicate_goal')}
          </WpDropdownMenuItem>
          {canChangeStatus && (
            <>
              <WpDropdownMenuItem onClick={toggleIsSettingsOpen}>
                <WpIcon name="cog" size="sm" />
                {tr('perform.goals.actions.goal_settings')}
              </WpDropdownMenuItem>
              <WpDropdownMenuDivider />
              <WpDropdownMenuItem color="danger" onClick={toggleIsDeleteOpen}>
                <WpIcon name="trash" size="sm" />
                {tr('perform.goals.buttons.delete_goal')}
              </WpDropdownMenuItem>
            </>
          )}
        </WpDropdownMenu>
      </WpDropdown>
      <GoalDeleteConfirmationModal goal={goal} isOpen={isDeleteOpen} toggleIsOpen={toggleIsDeleteOpen} />
      <GoalSettingsModal goal={goal} isOpen={isSettingsOpen} toggleIsOpen={toggleIsSettingsOpen} />
      <CreateGoalModal isOpen={isCloneOpen} toggleIsOpen={toggleIsCloneOpen} goalToClone={goal} />
    </>
  );
};

export default GoalActionsDropdown;
